<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import utils, { isH5 } from '@/utils'
import { useRouter, useRoute } from 'vue-router'
import { isMobile } from '@/utils'
import { useAppStore } from '@/store/useAppStore'
import { ElConfigProvider } from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { checkLicense } from '@/api'
import { checkIdentity } from '@/api/new-agent/qca'
import AiInitSetupDialog from '@/pages/ai-board/components/ai-menu/components/AiInitSetupDialog.vue'

const route = useRoute()
const router = useRouter()
const inxbb = ref(false)
const inxbbQuickView = ref(false)
const aiInitSetupDialogRef = ref(null)

// 全局唤起初始化配置弹窗
window.__openAiInitSetupDialog = (alias) => {
  if (isMobile()) return
  aiInitSetupDialogRef.value?.open(alias)
}

const isLoginPage = computed(() => {
  return ['appLoginIndex'].includes(route.name)
})
const showSafeArea = computed(() => {
  // App 主布局在菜单和内容内部避让安全区，让各自背景延伸到屏幕边缘。
  const hasAppLayout = isH5 && route.matched.some((record) => record.name === 'new-version-index')
  return route.name !== 'book' && !inxbb.value && !hasAppLayout
})

const hasUserInfo = () =>
  !!(utils.xbbLocalStorageGet('corpid') && utils.xbbLocalStorageGet('userId'))

// 解析hash中的参数
const parseHashQuery = () => {
  // 获取完整的hash（包含#）
  const hash = window.location.hash
  if (!hash) return {}

  // 提取#后的部分，并找到?开始的查询参数部分
  const hashContent = hash.slice(1) // 去掉#
  const [pathPart, queryStr] = hashContent.split('?')
  console.log('pathPart', pathPart)
  console.log('queryStr', queryStr)
  if (!queryStr) return {}

  // 解析query字符串为对象
  const queryParams = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    if (key && value) {
      queryParams[key] = decodeURIComponent(value)
    }
  })
  return queryParams
}

const init = async () => {
  if (
    window.location.hash.includes('book') ||
    window.location.hash.includes('describe') ||
    window.location.hash.includes('iframe') ||
    window.location.hash.includes('sign-form') ||
    window.location.hash.includes('download-page') ||
    window.location.hash.includes('pro-visit-detail') ||
    window.location.hash.includes('visit-path-detail')
  ) {
    return
  }
  const xbbAccessToken = utils.xbbLocalStorageGet('xbbAccessToken')

  try {
    if (xbbAccessToken) {
      const res = await checkLicense()
      const identityRes = await checkIdentity({ targetUserId: utils.xbbLocalStorageGet('userId') })
      if (res.result.useLimit) {
        // IF 判断一下，是否通过试用页面跳转过来的，如果是，则直接跳转到试用页面；
        if (route.query.trialApply && ['salesend', 'normal'].includes(route.query.source)) {
          router.push({
            name: 'trialApply',
            query: {
              userLimit: res.result.userLimit,
              source: route.query.source
            }
          })
          return
        }
        // ELSE IF 如果不是，则跳转到 404 页面，并且把后端返回的提示信息通过 query 传递过去
        router.push({
          name: '404',
          query: {
            memo: res.result.memo,
            setAccount: res.result.setAccount ? 1 : 0,
            setLicense: res.result.setLicense ? 1 : 0
          }
        })
        return
      }
      if (!identityRes.result.enabled) {
        router.push({
          name: '404',
          query: {
            memo: '用户身份校验失败',
            interface: encodeURIComponent(JSON.stringify(identityRes))
          }
        })
        return
      }
    }
  } catch (error) {
    console.log(error)
  }

  const cloudPath = utils.xbbLocalStorageGet('cloudPath')
  if (cloudPath && cloudPath !== 'undefined') {
    // pc 客户分析师
    utils.xbbLocalStorageRemove('cloudPath')
    router.push({
      path: cloudPath
    })
    return
  }

  if (xbbAccessToken && hasUserInfo()) {
    const hashQuery = parseHashQuery()
    console.log('hashQuery', hashQuery)
    if (hashQuery.pathAlias && router.hasRoute(hashQuery.pathAlias)) {
      const { pathAlias, firstAlias, ...restQuery } = hashQuery
      useAppStore().setDefaultMenu([firstAlias, pathAlias]) // 需要高亮一级菜单和二级菜单
      useAppStore().setThirdAppKey(hashQuery.appKey)
      router.push({
        name: pathAlias,
        query: { ...restQuery }
      })
    } else {
      const targetPath = isMobile() ? '/app/qca-chat' : '/ai-board/qoder-agent'
      router.replace({
        path: targetPath
      })
    }
  } else if (isMobile()) {
    router.replace({
      path: '/appLogin'
    })
  }
}

const h5HandlePlusready = () => {
  utils.h5HandlePlusready(route, router)
  init()
}

onMounted(() => {
  if (isH5) {
    if (window.plus) {
      h5HandlePlusready()
    } else {
      document.addEventListener('plusready', h5HandlePlusready, false)
    }
  } else {
    init()
  }
  // 获取完整 URL 中的搜索参数部分（即 "?inxbb=1"）
  const searchParams = new URLSearchParams(window.location.search)

  // 通过参数名 "inxbb" 获取对应的值
  const inxbbValue = searchParams.get('inxbb') || ''
  const quickview = searchParams.get('quickview') || ''
  inxbb.value = +inxbbValue === 1
  inxbbQuickView.value = +quickview === 1 && +inxbbValue === 1
})

onUnmounted(() => {
  document.removeEventListener('plusready', h5HandlePlusready, false)
  utils.removeBackbuttonListener?.()
})
</script>

<template>
  <el-config-provider :locale="zhCn">
    <div class="app-container">
      <div :class="showSafeArea ? 'safe-area-inset-top safe-area-inset-bottom' : ''">
        <RouterView />
      </div>
      <div v-if="!isLoginPage" class="gradient-background"></div>
    </div>
    <AiInitSetupDialog v-if="!isMobile()" ref="aiInitSetupDialogRef" />
  </el-config-provider>
</template>

<style scoped>
:deep(.el-dialog__headerbtn) {
  z-index: 5000 !important;
}
.app-container {
  height: 100%;
  z-index: 99;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  > div:first-child {
    z-index: 101;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100vh;
  }
}
</style>

<style lang="scss">
// 全局样式修改

body {
  --el-color-primary: #7a52f4;
  --el-color-primary-dark-2: #5132c6;

  --el-color-primary-light-3: #6543e5;
  --el-color-primary-light-5: #7a52f4;
  --el-color-primary-light-9: #fff;
  --el-text-color-placeholder: #c8c9cc;
  --el-border-radius-base: 8px;
  --el-input-border-radius: 8px;
  --el-dialog-title-font-size: 16px;
  --el-dialog-padding-primary: 16px 24px;
  --el-text-color-regular: #242832;
  --el-button-disabled-text-color: #a9b2c3;
  --el-border-color: #e5e8f5;

  // Tag标签
  .el-tag {
    color: #323233;
    background-color: #f2f3f5;
  }
  .el-drawer__header {
    margin-bottom: 0;
    border-bottom: 1px solid #f2f3f5;
    padding-bottom: 16px; //覆盖默认的
  }
  .el-drawer__footer {
    border-top: 1px solid #f2f3f5;
  }
  .el-button {
    font-weight: normal;
  }
}
</style>
